/**
 * @fileoverview EandOPolicyUploader - PDF Certificate Upload Component
 *
 * Handles E&O insurance certificate PDF upload, display, download, and deletion.
 * Shows file uploader before upload, thumbnail + download button after upload.
 *
 * Features:
 * - File upload with validation (size, type)
 * - PDF thumbnail display after successful upload
 * - Download button to view uploaded certificate
 * - Delete button with confirmation modal
 * - Group policy link display (signature agents only)
 * - Upload progress indicator
 *
 * Upload Workflow:
 * 1. User selects PDF file from file input
 * 2. File validated (size < 10MB, type = PDF/image)
 * 3. Read as base64 via FileReader
 * 4. Upload to server via Store.onSelectFile()
 * 5. Server saves to Firebase Storage
 * 6. Database updated with PDF URL
 * 7. UI switches to thumbnail + download view
 *
 * Delete Workflow:
 * 1. User clicks trash icon
 * 2. Confirmation modal appears
 * 3. User confirms deletion
 * 4. Store.deletePolicyPdf() clears eopolicy_pdf field
 * 5. Database updated (URL removed)
 * 6. UI switches back to file uploader
 *
 * Related Documentation:
 * - /docs/modules/AgentEandOManager/components.md - Component details
 * - /docs/modules/AgentEandOManager/user-flows.md - Upload workflows
 *
 * @module components/agents/AgentEandOManager/EandOPolicyUploader
 * @requires react
 * @requires mobx-react-lite
 */

import React from 'react'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import {
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalBody,
  MDBModalTitle,
} from 'mdb-react-ui-kit'
import UserProfileService from './../../../shared/services/UserProfile.service'
import EopolicyStore from './Eopolicy.store'
import SecureFile from './../../../shared/services/SecureFile.service'
import AgentProfileStore from './../AgentProfileIntake/AgentProfile.store'
import exErrorOmission from './assets/imgs/img.errors-omissions.png'

import './EandOPolicyUploader.component.scss'
import MDB4FileInput from '../../forms/MDB4FileInput'

/**
 * Download PDF from Firebase Storage
 *
 * Opens PDF in new browser tab using Firebase Storage public URL.
 * Constructs full Firebase Storage URL from relative path.
 *
 * @private
 * @param {string} url - Relative path to PDF in Firebase Storage
 *
 * @example
 * // Policy has eopolicy_pdf = 'eopolicies/15560_1638360000000.pdf'
 * downloadPdf('eopolicies/15560_1638360000000.pdf')
 * // Opens: https://firebasestorage.googleapis.com/v0/b/.../eopolicies%2F15560_1638360000000.pdf?alt=media
 */
const downloadPdf = async url => {
  if (!url) return
  const w = window.open('', '_blank') // open in the click gesture (avoids popup block)
  const signed = await SecureFile.getUrl(url)
  if (signed && w) w.location.href = signed
  else if (w) w.close()
}

/**
 * PDF Upload, Display, and Management Component
 *
 * Renders file uploader (before upload) or thumbnail + download button (after upload).
 * Includes delete functionality with confirmation modal and group policy link.
 *
 * Display States:
 * 1. **Not Uploaded:** Shows file input button "Upload E&O Certificate"
 * 2. **Uploading:** Button shows "Uploading..." (disabled)
 * 3. **Uploaded:** Shows thumbnail image + "Download E&O Policy" button + trash icon
 * 4. **Policy Not Saved:** Upload button disabled (requires Policy.id)
 *
 * UI Elements:
 * - File Input: MDBFileInput with PDF/image accept filter
 * - Thumbnail: Static E&O certificate image (exErrorOmission)
 * - Download Button: Opens PDF in new tab via downloadPdf()
 * - Delete Icon: Trash icon (top-right) opens confirmation modal
 * - Group Policy Link: "View Group E&O Certificate" (signature agents only)
 * - Confirmation Modal: Delete confirmation with carrier name
 *
 * Validation:
 * - File size: Max 10MB (enforced by component logic)
 * - File type: PDF, JPEG, PNG (accept="image/*")
 * - Policy must be saved first (Policy.id required)
 *
 * @component
 * @param {object} props - Component props
 * @param {object} props.Policy - UserEopolicy model instance
 * @param {function} [props.onAutoSave] - Callback after upload: () => void
 *
 * @returns {JSX.Element} Upload UI or empty fragment if no Policy
 *
 * @example
 * // In EandOForm
 * <EandOPolicyUploader
 *   Policy={Store.Policy}
 *   onAutoSave={() => {
 *     const isValid = Store.getIsValid()
 *     onAutoSave(isValid)
 *   }}
 * />
 *
 * @example
 * // Before upload state
 * <div className="img-wrapper">
 *   <MDBFileInput
 *     btnTitle="Upload E&O Certificate"
 *     disabled={false}
 *   />
 * </div>
 *
 * @example
 * // After upload state
 * <div className="img-wrapper">
 *   <div className="delete-btn">🗑️</div>
 *   <img src="..." alt="Agent's E&O Policy" />
 *   <small>Uploaded</small>
 *   <div className="download-btn">
 *     📥 Download E&O Policy
 *   </div>
 * </div>
 */
const EandOPolicyUploader = props => {
  const Policy = props.Policy,
    onAutoSave = props.onAutoSave
  const { getGroupUrl, showModal, deletePolicyPdf, isDeleting, onSelectFile, getIsValid, isSaving, isUploading } =
    EopolicyStore
  if (!Policy) return <></>

  // Check if user is BST (internal-admin, internal-staff, or system-admin)
  const isBstUser = UserProfileService.isA(['internal-admin', 'internal-staff', 'system-admin'], true)

  // Check if the currently selected policy type is company-supplied
  // Access policyType directly from store to maintain MobX reactivity
  const isCompanySupplied = EopolicyStore.policyType === 'company'

  // Disable upload for non-BST users ONLY when policy is company-supplied
  const isUploadDisabled = isCompanySupplied && !isBstUser

  const renderFileOutput = () => {
    return (
      <div className='img-wrapper'>
        {Policy?.get('eopolicy_pdf') && `${Policy.get('eopolicy_pdf')}`.trim().length > 0 ? (
          <>
            <div onClick={() => (EopolicyStore.showModal = true)} className='delete-btn'>
              <MDBIcon icon='trash' className='remove' />
            </div>
            <div className='img'>
              <img src={exErrorOmission} alt="Agent's E&O Policy" />
              <small>Uploaded</small>
            </div>
            <div onClick={() => downloadPdf(`${Policy.get('eopolicy_pdf')}`.trim())} className='download-btn'>
              <MDBIcon icon='download' />
              &nbsp;Download E&O Policy
            </div>
          </>
        ) : (
          <>
            <MDB4FileInput
              disabled={!Policy?.id() || isSaving || isUploading || isUploadDisabled}
              name='eopolicy_pdf'
              accept='image/*,.pdf,application/pdf'
              onChange={e => {
                const file = e.target.files?.[0]
                if (!file || !(file instanceof File)) return
                onSelectFile(file).finally(() => onAutoSave())
              }}
              btnTitle={isUploading ? 'Uploading ...' : 'Upload E&O Certificate'}
              textFieldTitle={'Your Policy File'}
            />
          </>
        )}
      </div>
    )
  }

  const renderGroupPolicy = () => {
    if (UserProfileService.isA(['affiliate-group', 'associate-group'])) return <></>

    if (getGroupUrl())
      return (
        <div className='group-policy-wrapper'>
          <a className='btn view-group-policy-btn' href={getGroupUrl()} target='_BLANK' rel='noopener noreferrer'>
            View Group E&O Certificate
          </a>
        </div>
      )
  }

  return (
    <div
      className={[
        'EandOPolicyUploaderComponent input-wrapper upload-input-wrapper',
        !Policy?.get('eopolicy_pdf') ? 'active-input' : '',
        !Policy?.id() || isUploadDisabled ? 'disabled-input' : '',
        Policy?.id() && !Policy?.get('eopolicy_pdf') && !isUploadDisabled ? 'invalid-input' : '',
      ].join(' ')}
    >
      {renderFileOutput()}
      {renderGroupPolicy()}
      <MDBModal open={showModal} onClose={() => (EopolicyStore.showModal = false)} className='confirm-delete-modal'>
        <MDBModalDialog size='sm' className='confirm-delete-modal-dialog'>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Confirm Delete?</MDBModalTitle>
              <MDBBtn
                type='button'
                className='btn-close'
                color='none'
                onClick={() => (EopolicyStore.showModal = false)}
              ></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <h5 className='text-center'>
                <strong>Are you sure you want to delete the {Policy?.get('carrier_name')} E&O Policy Document?</strong>
              </h5>
              <br />
              {Policy?.get('eopolicy_pdf') && (
                <MDBBtn
                  type='button'
                  color='default'
                  block
                  disabled={isDeleting}
                  onClick={() => {
                    deletePolicyPdf().then(
                      msg =>
                        toast.success('The E&O policy document has been deleted.', {
                          position: toast.POSITION.TOP_RIGHT,
                        }),
                      err =>
                        toast.error(`${err}`, {
                          position: toast.POSITION.TOP_RIGHT,
                        })
                    )
                    AgentProfileStore.setValidity('eopolicy', getIsValid())
                  }}
                  className='confirm-delete-btn'
                >
                  Yes, Delete E&O Policy Document
                </MDBBtn>
              )}
              <MDBBtn
                type='button'
                color='default'
                block
                disabled={isDeleting}
                onClick={() => (EopolicyStore.showModal = false)}
                className='confirm-cancel-btn'
              >
                Cancel
              </MDBBtn>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  )
}

export default observer(EandOPolicyUploader)
